import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  useGetAppVideoScreensPaginatedQuery,
  useGetVideoStoreScrollQuery,
  useHiddenAppsMutation,
} from "@/features/auth/api/authAPI";
import { useMe, useCurrentOrganization } from "@/Hooks";

import { useNavigate } from "react-router-dom";
import styles from "./MultipuleSelect.module.scss";
import PhoneHomePageHorisontalNew from "@/Views/Virtual/PhoneHomePageHorisontalNew";
import Modal from "react-modal";
import ContentLoader from "react-content-loader";
import { motion, AnimatePresence } from "framer-motion";
import { getFilterAppsData } from "@/entities/filterAppsData/model/selectors/getFilterAppsData";
import { useDispatch, useSelector } from "react-redux";
import PhoneHomePageStore from "@/widgets/PhoneHomePageStore/ui/PhoneHomePageStore/PhoneHomePageStore";
import { getButtonFilter } from "@/entities/buttonFilter";

import { handleOpenVideoPage } from "@/app/libs/const/handleOpenVideoPage";
import { useSelectedScreens } from "@/entities/selectedScreens";
import { SIMILAR_SCREEN_STORAGE_KEY } from "@/Views/SimilarScreen/SimilarScreenModal/SimilarScreenModal";
import { VideoLinkProvider } from "@/app/providers/video/VideoLinkProvider";
import { useWindowWidth } from "@/Hooks/useWindowWidth";
import useEmblaCarousel from "embla-carousel-react";
import type { EmblaCarouselType } from "embla-carousel";
import mixpanel from "@/shared/lib/mixpanel";
import { toast } from "react-toastify";
import MenuButtons from "@/widgets/phoneHomePageHorisontal/ui/MenuButtons/MenuButtons";
import { filterAppsDataActions } from "@/entities/filterAppsData";

interface Screen {
  id: number;
  screen: string;
  timestamp: string;
  is_saved?: boolean;
  is_saved_pk?: number;
  app_video?: number;
}

interface AppDataFilter {
  id: number;
  name: string;
  shortname: string;
  icon: string;
  latest_appvideo_id: number;
}

interface MultipuleSelectProps {
  appDataFilter: AppDataFilter;
  currentAppIndex: number | null;
  setCurrentAppIndex: (index: number) => void;
  appIndex: number;
  onNextApp: (index: number) => void;
  appDataToShow: any;
  onPreviousApp: (index: number) => void;
  currentScreenNotBlurredID?: number | null;
  setCurrentScreenNotBlurredID?: (id: number | null) => void;
  savedReplayScreensMap?: Record<
    number,
    { is_saved: boolean; is_saved_pk: number | null }
  >;
}

const MultipuleSelect: React.FC<MultipuleSelectProps> = ({
  appDataFilter,
  currentAppIndex,
  setCurrentAppIndex,
  appIndex,
  onNextApp,
  appDataToShow,
  onPreviousApp,
  currentScreenNotBlurredID,
  setCurrentScreenNotBlurredID,
  savedReplayScreensMap,
}) => {
  const { me } = useMe();
  const navigate = useNavigate();
  const [pageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [isHovered2, setIsHovered2] = useState(false);
  const currenOrganization = useCurrentOrganization();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [screens, setScreens] = useState<Screen[]>([]);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const { activeIndex } = useSelector(getButtonFilter);
  const [slideDirection, setSlideDirection] = useState<"left" | "right">(
    "right",
  );
  const [transitionDirection, setTransitionDirection] = useState<
    "next" | "prev" | null
  >(null);
  const { filterAppDataSorted, isScreensLoadingHome, isScreensLoadingStore } =
    useSelector(getFilterAppsData);
  const [preloadedImages, setPreloadedImages] = useState<{
    [key: number]: boolean;
  }>({});
  const [isImageError, setIsImageError] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const {
    addScreen,
    removeScreen,
    isSelected: checkIsSelected,
    selectedScreens,
  } = useSelectedScreens();

  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 640;

  // Embla Carousel setup (only for mobile)
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "center",
    skipSnaps: false,
    dragFree: false,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const {
    data: paginatedHomeScreens,
    isFetching: isHomeScreensFetching,
    isLoading: isHomeScreensLoading,
    error: homeScreensError,
  } = useGetAppVideoScreensPaginatedQuery(
    {
      appId: undefined, // use app_video instead
      pageSize: pageSize,
      page: page,
      // featured_only: true,
      app_video: appDataFilter?.latest_appvideo_id,
    },
    {
      skip: !appDataFilter?.id || !hasMore || activeIndex !== 0,
    },
  );

  const {
    data: paginatedStoreScreens,
    isFetching: isStoreScreensFetching,
    isLoading: isStoreScreensLoading,
    error: storeScreensError,
  } = useGetVideoStoreScrollQuery(
    {
      id: appDataFilter?.id,
      page: page,
    },
    {
      skip: !appDataFilter?.id || !hasMore || activeIndex !== 1,
    },
  );

  const isScreensFetching =
    activeIndex === 0 ? isHomeScreensFetching : isStoreScreensFetching;
  const isScreensLoading =
    activeIndex === 0 ? isHomeScreensLoading : isStoreScreensLoading;
  const screensError = activeIndex === 0 ? homeScreensError : storeScreensError;
  const paginatedScreens =
    activeIndex === 0 ? paginatedHomeScreens : paginatedStoreScreens;

  const handlePrev = () => {
    setSlideDirection("left");
    setCurrentIndex((prevIndex) => {
      const newIndex = prevIndex === 0 ? screens.length - 1 : prevIndex - 1;
      return newIndex;
    });
  };

  const handleNext = () => {
    setSlideDirection("right");
    setCurrentIndex((prevIndex) => {
      const newIndex = prevIndex === screens.length - 1 ? 0 : prevIndex + 1;
      return newIndex;
    });

    if (screens.length - currentIndex <= 5 && hasMore) {
      loadMore();
    } else {
      for (let i = 1; i <= 3; i++) {
        const futureIndex = (currentIndex + i) % screens.length;
        if (screens[futureIndex]?.screen) {
          preloadImage(screens[futureIndex].screen, futureIndex);
        }
      }
    }
  };

  const handleOpenModal = () => {
    setCurrentAppIndex(appIndex);
    if (setCurrentScreenNotBlurredID && screens[currentIndex]) {
      setCurrentScreenNotBlurredID(screens[currentIndex].id);
    }
    setIsModalOpen((prev) => !prev);
  };

  const handlePreviousApp = () => {
    if (
      setCurrentScreenNotBlurredID &&
      filterAppDataSorted[currentAppIndex - 1]
    ) {
      // We need to set the first screen ID of the previous app
      const prevAppScreens = filterAppDataSorted[currentAppIndex - 1];
      if (prevAppScreens) {
        setCurrentScreenNotBlurredID(prevAppScreens.id);
      }
    }
    setTransitionDirection("prev");
    onPreviousApp(currentAppIndex);
  };

  const handleNextApp = () => {
    if (
      setCurrentScreenNotBlurredID &&
      filterAppDataSorted[currentAppIndex + 1]
    ) {
      // We need to set the first screen ID of the next app
      const nextAppScreens = filterAppDataSorted[currentAppIndex + 1];
      if (nextAppScreens) {
        setCurrentScreenNotBlurredID(nextAppScreens.id);
      }
    }
    setTransitionDirection("next");
    onNextApp(currentAppIndex);
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.5, x: "-50%", y: "-50%" },
    visible: { opacity: 1, scale: 1, x: "-50%", y: "-50%" },
    exit: { opacity: 0, scale: 0.5, x: "-50%", y: "-50%" },
  };

  const contentVariants = {
    hidden: (direction: "next" | "prev") => ({
      opacity: 0,
      y: direction === "next" ? 100 : -100,
    }),
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
    exit: (direction: "next" | "prev") => ({
      opacity: 0,
      y: direction === "next" ? -100 : 100,
      transition: {
        duration: 0.5,
        ease: "easeIn",
      },
    }),
  };

  const imageVariants = {
    enter: (direction: string) => ({
      x: direction === "right" ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: string) => ({
      x: direction === "right" ? -300 : 300,
      opacity: 0,
    }),
  };

  const switchVariants = {
    hidden: (direction: "next" | "prev") => ({
      opacity: 0,
      y: direction === "next" ? 100 : -100,
      scale: 0.8,
    }),
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    exit: (direction: "next" | "prev") => ({
      opacity: 0,
      y: direction === "next" ? -100 : 100,
      scale: 0.8,
      transition: {
        duration: 0.2,
        ease: "easeIn",
      },
    }),
  };

  const loadMore = useCallback(() => {
    if (hasMore && !isScreensFetching) {
      setPage((prev) => prev + 1);
    }
  }, [hasMore, isScreensFetching]);

  const preloadImage = useCallback(
    (imageUrl: string, index: number) => {
      if (!imageUrl || preloadedImages[index]) return;

      const img = new Image();
      img.src = imageUrl;
      img.onload = () => {
        setPreloadedImages((prev) => ({ ...prev, [index]: true }));
      };
      img.onerror = () => {
        console.error(`Failed to preload image at index ${index}`);
      };
    },
    [preloadedImages],
  );

  useEffect(() => {
    if (screens.length === 0) return;

    preloadImage(screens[currentIndex]?.screen, currentIndex);

    const nextIndex =
      currentIndex === screens.length - 1 ? 0 : currentIndex + 1;
    if (screens[nextIndex]?.screen) {
      preloadImage(screens[nextIndex].screen, nextIndex);
    }

    const prevIndex =
      currentIndex === 0 ? screens.length - 1 : currentIndex - 1;
    if (screens[prevIndex]?.screen) {
      preloadImage(screens[prevIndex].screen, prevIndex);
    }
  }, [currentIndex, screens, preloadImage]);

  useEffect(() => {
    setPage(1);
    setScreens([]);
    setCurrentIndex(0);
    setHasMore(true);
    setShowSkeleton(true);
  }, [activeIndex, appDataFilter?.id]);

  useEffect(() => {
    if (paginatedScreens?.results?.length > 0) {
      setScreens((prevScreens) => {
        if (page === 1) {
          const newScreens =
            activeIndex === 0
              ? paginatedScreens.results.map((screen) => ({
                  id: screen.id,
                  screen: screen.screen,
                  timestamp: screen.timestamp,
                  is_saved: screen.is_saved,
                  is_saved_pk: screen.is_saved_pk,
                  app_video: screen.app_video,
                }))
              : paginatedScreens.results.map((screen) => ({
                  id: screen.id,
                  screen: screen.image,
                  timestamp: "",
                  is_saved: screen.is_saved || false,
                  is_saved_pk: screen.is_saved_pk || null,
                  app_video: undefined,
                }));
          return newScreens;
        }

        const existingIds = new Set(prevScreens.map((screen) => screen.id));

        const newScreens =
          activeIndex === 0
            ? paginatedScreens.results
                .filter((screen) => !existingIds.has(screen.id))
                .map((screen) => ({
                  id: screen.id,
                  screen: screen.screen,
                  timestamp: screen.timestamp,
                  is_saved: screen.is_saved,
                  is_saved_pk: screen.is_saved_pk,
                  app_video: screen.app_video,
                }))
            : paginatedScreens.results
                .filter((screen) => !existingIds.has(screen.id))
                .map((screen) => ({
                  id: screen.id,
                  screen: screen.image,
                  timestamp: "",
                  is_saved: screen.is_saved || false,
                  is_saved_pk: screen.is_saved_pk || null,
                  app_video: undefined,
                }));
        return [...prevScreens, ...newScreens];
      });

      // Handle pagination
      if (activeIndex === 0) {
        setHasMore(!!paginatedScreens.next);
      } else {
        const receivedItems = paginatedScreens.results.length;
        setHasMore(receivedItems === pageSize);
      }
    }
  }, [paginatedScreens, activeIndex, page, pageSize]);

  useEffect(() => {
    if (screens.length > 0 && !isScreensLoading) {
      setShowSkeleton(false);
    }
  }, [screens, isScreensLoading]);

  useEffect(() => {
    if (screensError) {
      if ("status" in screensError && screensError.status === 404) {
        setHasMore(false);
      }
      console.error("Error fetching video screens:", screensError);
      setShowSkeleton(false);
    }
  }, [screensError]);

  useEffect(() => {
    if (currentAppIndex > appIndex) {
      setTransitionDirection("next");
    } else if (currentAppIndex < appIndex) {
      setTransitionDirection("prev");
    }
  }, [currentAppIndex, appIndex]);

  useEffect(() => {
    if (
      savedReplayScreensMap &&
      Object.keys(savedReplayScreensMap).length > 0
    ) {
      setScreens((prevScreens) => {
        const updatedScreens = prevScreens.map((screen) => {
          if (savedReplayScreensMap[screen.id]) {
            return {
              ...screen,
              is_saved: savedReplayScreensMap[screen.id].is_saved,
              is_saved_pk: savedReplayScreensMap[screen.id].is_saved_pk,
            };
          }
          return screen;
        });
        return updatedScreens;
      });
    }
  }, [savedReplayScreensMap]);

  // Handle selection for the current image
  const handleSelectImage = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (!screens[currentIndex]) return;

    // Get the latest saved status from savedReplayScreensMap if available
    const isSaved =
      savedReplayScreensMap && savedReplayScreensMap[screens[currentIndex].id]
        ? savedReplayScreensMap[screens[currentIndex].id].is_saved
        : screens[currentIndex].is_saved;

    const savedPk =
      savedReplayScreensMap && savedReplayScreensMap[screens[currentIndex].id]
        ? savedReplayScreensMap[screens[currentIndex].id].is_saved_pk
        : screens[currentIndex].is_saved_pk;

    if (checkIsSelected(screens[currentIndex].id)) {
      removeScreen(screens[currentIndex].id);
      setIsSelected(false);
    } else {
      addScreen({
        id: screens[currentIndex].id,
        ts: screens[currentIndex].timestamp,
        app_video: screens[currentIndex].app_video,
        isSaved: isSaved,
        is_saved_pk: savedPk,
      });
      setIsSelected(true);
    }
  };

  // Handle similar screen functionality
  const handleSimilarClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (!screens[currentIndex]) return;

    const currentScreen = screens[currentIndex];

    const similarScreenData = {
      id: currentScreen.id,
      screen: currentScreen.screen,
      appId: currentScreen.app_video,
      timestamp: currentScreen.timestamp,
      appIcon: appDataFilter.icon || "",
      appName: appDataFilter.name || "",
      appDescription: appDataFilter.shortname || "",
    };

    localStorage.setItem(
      SIMILAR_SCREEN_STORAGE_KEY,
      JSON.stringify(similarScreenData),
    );

    window.open(`/similar-screens/${currentScreen.id}`, "_blank");
  };

  // Update isSelected when selectedScreens changes
  useEffect(() => {
    if (screens.length > 0 && currentIndex < screens.length) {
      setIsSelected(checkIsSelected(screens[currentIndex].id));
    }
  }, [selectedScreens, screens, currentIndex, checkIsSelected]);

  // Embla callbacks
  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  const onScroll = useCallback(
    (emblaApi: EmblaCarouselType) => {
      const currentIndex = emblaApi.selectedScrollSnap();
      // Load more when approaching the end
      if (screens.length - currentIndex <= 3 && hasMore) {
        loadMore();
      }
    },
    [screens.length, hasMore, loadMore],
  );

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi],
  );

  // Get visible dots for mobile (5 closest)
  const getVisibleDots = () => {
    if (screens.length <= 5) {
      return screens.map((_, index) => index);
    }

    const totalDots = 5;
    const half = Math.floor(totalDots / 2);

    let start = selectedIndex - half;
    let end = selectedIndex + half;

    if (start < 0) {
      start = 0;
      end = totalDots - 1;
    }

    if (end >= screens.length) {
      end = screens.length - 1;
      start = Math.max(0, screens.length - totalDots);
    }

    const indices = [];
    for (let i = start; i <= end; i++) {
      indices.push(i);
    }

    return indices;
  };

  // Embla effects
  useEffect(() => {
    if (!emblaApi || !isMobile) return;

    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);
    emblaApi.on("scroll", onScroll);

    return () => {
      emblaApi.off("reInit", onSelect);
      emblaApi.off("select", onSelect);
      emblaApi.off("scroll", onScroll);
    };
  }, [emblaApi, onSelect, onScroll, isMobile]);

  const infoModalRef = useRef(null);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        infoModalRef.current &&
        !infoModalRef.current.contains(event.target as Node)
      ) {
        setIsInfoModalOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const dispatch = useDispatch();
  const [hideApp] = useHiddenAppsMutation();
  const handleHideClick = async () => {
    const isUserLogin = localStorage.getItem("access_token");

    if (!isUserLogin) {
      navigate("/login");
      localStorage.setItem("btnName", "hide");
      return;
    }

    // Track the hide app button click event
    mixpanel.track("Hide App Button Clicked", {
      idApp: appDataFilter.id,
      app_name: appDataFilter.name,
      UserEmail: me?.email || "",
      OrganizationID: currenOrganization?.uuid || "",
      source: "ExploreReplayRow",
    });

    try {
      await hideApp({
        app: appDataFilter.id,
      }).unwrap();

      // Update user profile with hidden apps count
      if (me?.email) {
        mixpanel.identify(me.email);
        setTimeout(() => {
          mixpanel.people.increment("total_hidden_apps", 1);
        }, 100);
      }

      dispatch(filterAppsDataActions.removeAppFromData(appDataFilter.id));
      toast.success("App hidden successfully");
    } catch (error) {
      console.error("Error hiding app:", error);
      toast.error("Failed to hide app");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.infoContainerWrapper}>
        <div className={styles.infoContainer}>
          <img className={styles.icon} src={appDataFilter.icon} alt="Icon" />
          <VideoLinkProvider
            slugText={appDataFilter.slug}
            appId={appDataFilter.id}
            videoId={appDataFilter.latest_appvideo_id}
            userName={me?.username}
            organizationId={currenOrganization?.uuid}
            from="Explore Apps Single View"
          >
            <div className={styles.textContainer}>
              <h5>{appDataFilter.name}</h5>
              <h6>{appDataFilter.shortname}</h6>
            </div>
          </VideoLinkProvider>
        </div>
        <div
          className={styles.dots}
          ref={infoModalRef}
          onClick={() => setIsInfoModalOpen(!isInfoModalOpen)}
        >
          <img src="/img/blackIcon/Plus.svg" alt="plus" />
          {isInfoModalOpen && (
            <div className={styles.infoModal}>
              <MenuButtons
                handleHideClick={handleHideClick}
                isStarClickLoading={false}
                appId={appDataFilter.id}
                onClose={() => setIsInfoModalOpen(false)}
              />
            </div>
          )}
        </div>
      </div>

      {isMobile ? (
        // Mobile: Embla Carousel
        <div className={styles.embla}>
          <div className={styles.emblaViewport} ref={emblaRef}>
            <div className={styles.emblaContainer}>
              {showSkeleton ? (
                <div className={styles.emblaSlide}>
                  <ContentLoader
                    speed={2}
                    width={250}
                    height={420}
                    viewBox="0 0 250 420"
                    backgroundColor="#f3f3f3"
                    foregroundColor="#ecebeb"
                  >
                    <rect
                      x="0"
                      y="0"
                      rx="20"
                      ry="20"
                      width="100%"
                      height="100%"
                    />
                  </ContentLoader>
                </div>
              ) : (
                screens.map((screen, index) => (
                  <div key={screen.id} className={styles.emblaSlide}>
                    <div className={styles.emblaSlideImgWrapper}>
                      <img
                        src={screen.screen}
                        alt={`Screenshot ${index + 1}`}
                        className={styles.emblaSlideImg}
                        onError={() => setIsImageError(true)}
                        loading="lazy"
                      />
                    </div>
                    {isImageError && (
                      <div className={styles.errorContainer}>
                        <p>Failed to load image</p>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Mobile Dots Navigation */}
          {screens.length > 1 && !showSkeleton && (
            <div className={styles.emblaDots}>
              {getVisibleDots().map((index) => (
                <button
                  key={index}
                  className={`${styles.emblaDot} ${
                    index === selectedIndex ? styles.emblaDotSelected : ""
                  }`}
                  type="button"
                  onClick={() => scrollTo(index)}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        // Desktop: Existing layout
        <div
          className={styles.containerImg}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div
            className={`${styles.arrowIcon} ${isHovered && currentIndex > 0 ? styles.visible : ""}`}
            onClick={currentIndex > 0 ? handlePrev : undefined}
            style={{ pointerEvents: currentIndex > 0 ? "auto" : "none" }}
          >
            <img src="/img/blackIcon/ArrowLeft.svg" alt="Arrow Left" />
          </div>

          {showSkeleton ? (
            <div className={styles.imgContainer}>
              <ContentLoader
                speed={2}
                width={340}
                height={550}
                viewBox="0 0 340 550"
                backgroundColor="#f3f3f3"
                foregroundColor="#ecebeb"
              >
                <rect x="0" y="0" rx="7" ry="7" width="340" height="550" />
              </ContentLoader>
            </div>
          ) : (
            screens.length > 0 && (
              <div className={styles.imgContainer}>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={currentIndex}
                    className={styles.imageWrapper}
                    variants={imageVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    custom={slideDirection}
                    transition={{
                      duration: 0.2,
                      ease: "easeInOut",
                    }}
                  >
                    <img
                      src={screens[currentIndex]?.screen}
                      alt="Screenshot"
                      className={`${styles.firstimg} ${imageLoaded ? styles.loaded : ""}`}
                      onClick={handleOpenModal}
                      onLoad={() => {
                        setImageLoaded(true);
                        setShowSkeleton(false);
                        setIsImageError(false);
                      }}
                      onError={() => {
                        setIsImageError(true);
                        setImageLoaded(true);
                        setShowSkeleton(false);
                      }}
                      loading="eager"
                      decoding="async"
                      fetchpriority="high"
                    />
                    {isImageError && (
                      <div className={styles.errorContainer}>
                        <p>Failed to load image</p>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>

                {(!imageLoaded || showSkeleton) && (
                  <ContentLoader
                    speed={1}
                    width={340}
                    height={550}
                    viewBox="0 0 340 550"
                    backgroundColor="#f3f3f3"
                    foregroundColor="#ecebeb"
                    style={{ position: "absolute", top: 0, left: 0 }}
                  >
                    <rect x="0" y="0" rx="7" ry="7" width="340" height="550" />
                  </ContentLoader>
                )}

                {(isHovered || isSelected) && (
                  <div className={styles.selectButtonContainer}>
                    <div className={styles.selectButtonContainerInner}>
                      {isHovered && screens[currentIndex]?.is_saved && me && (
                        <div className={styles.savedTag}>
                          <p>Saved</p>
                        </div>
                      )}

                      <button
                        className={`${styles.selectButton} ${isSelected ? styles.selected : ""}`}
                        onClick={handleSelectImage}
                      >
                        {isSelected && (
                          <img
                            src="/img/blackIcon/Check.svg"
                            alt="selectImage"
                          />
                        )}
                      </button>
                    </div>
                  </div>
                )}
                {isHovered && (
                  <div className={styles.bottomMenuContainer}>
                    <div className={styles.bottomMenuContainerInner}>
                      <button onClick={handleSimilarClick}>
                        <img src="/img/similar-icon.svg" alt="similar" />
                        <p>Similar</p>
                      </button>
                      <button
                        onClick={() =>
                          handleOpenVideoPage(
                            0,
                            appDataFilter.slug,
                            "HomePage",
                            1,
                            appDataFilter.id,
                            appDataFilter.latest_appvideo_id,
                          )
                        }
                      >
                        <img src="/img/see-video-icon.svg" alt="see-video" />
                        <p>See Video</p>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          )}

          <div
            className={`${styles.arrowIcon} ${isHovered ? styles.visible : ""}`}
            onClick={handleNext}
          >
            <img src="/img/blackIcon/ArrowRight.svg" alt="Arrow Right" />
          </div>
        </div>
      )}

      <AnimatePresence mode="wait">
        {isModalOpen && !isScreensLoadingHome && !isScreensLoadingStore && (
          <Modal
            isOpen={isModalOpen}
            onRequestClose={handleOpenModal}
            contentLabel="Image Modal"
            className={styles.modal}
            overlayClassName={styles.overlay}
          >
            <motion.div
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={modalVariants}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              style={{ position: "absolute", top: "50%", left: "50%" }}
            >
              <motion.div
                key={currentAppIndex}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={contentVariants}
                custom={transitionDirection}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                <motion.div>
                  {currentAppIndex > 0 && (
                    <div className={styles.prevAppContainer}>
                      <button
                        className={styles.previusAppButton}
                        onClick={() => handlePreviousApp(currentAppIndex)}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                      >
                        <img
                          src="/img/blackIcon/ArrowRight.svg"
                          alt="Arrow Right"
                        />
                      </button>
                      <p
                        className={`${styles.previusAppText} ${isHovered ? styles.visible : ""}`}
                      >
                        Go to previous app
                      </p>
                    </div>
                  )}

                  <AnimatePresence mode="wait" custom={transitionDirection}>
                    <motion.div
                      key={activeIndex}
                      custom={transitionDirection}
                      variants={switchVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      style={{ width: "100%" }}
                    >
                      {activeIndex === 0 ? (
                        <PhoneHomePageHorisontalNew
                          dataApi={
                            appIndex === currentAppIndex
                              ? appDataFilter
                              : appDataToShow
                          }
                          isOpen={false}
                          setIsPhonePageLoading={() => {}}
                          setIsSaveScreen={() => {}}
                          setLastClickTime={() => {}}
                          isFirstGroup={false}
                          currentOrganization={currenOrganization}
                          isPaywallOnly={false}
                          isPhonePageLoading={false}
                          ScreenNotBlurredID={currentScreenNotBlurredID}
                        />
                      ) : (
                        <PhoneHomePageStore
                          organization={currenOrganization.uuid}
                          key={appDataFilter.id}
                          dataApi={
                            appIndex === currentAppIndex
                              ? appDataFilter
                              : appDataToShow
                          }
                          apiDataSavedStoreScreens={{
                            count: 0,
                            next: null,
                            previous: null,
                            results: [],
                          }}
                          isLoadingStore={false}
                          isFetchingStore={false}
                          ScreenNotBlurredID={currentScreenNotBlurredID}
                        />
                      )}
                    </motion.div>
                  </AnimatePresence>

                  {filterAppDataSorted.length > currentAppIndex + 1 && (
                    <div className={styles.nextAppContainer}>
                      <button
                        className={styles.nextAppButton}
                        onClick={() => handleNextApp(currentAppIndex)}
                        onMouseEnter={() => setIsHovered2(true)}
                        onMouseLeave={() => setIsHovered2(false)}
                      >
                        <img
                          src="/img/blackIcon/ArrowRight.svg"
                          alt="Arrow Right"
                        />
                      </button>
                      <p
                        className={`${styles.nextAppText} ${isHovered2 ? styles.visible : ""}`}
                      >
                        Go to next app
                      </p>
                    </div>
                  )}
                </motion.div>
              </motion.div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MultipuleSelect;
